<template>
  <el-dialog
    :model-value="modelValue"
    width="420px"
    class="login-dialog"
    modal-class="login-overlay"
    :close-on-click-modal="false"
    append-to-body
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <template #header>
      <div class="login-head">
        <span class="eyebrow">Secure Access</span>
        <strong>{{ authMode === 'login' ? '探索者登录' : '注册探索者' }}</strong>
        <p>进入层级网络后可占领、收藏、留言与编辑自己的层级档案。</p>
      </div>
    </template>

    <div class="auth-switch">
      <button type="button" class="auth-tab" :class="{ active: authMode === 'login' }" @click="$emit('update:authMode', 'login')">登录</button>
      <button type="button" class="auth-tab" :class="{ active: authMode === 'register' }" @click="$emit('update:authMode', 'register')">注册</button>
    </div>

    <el-form ref="loginFormRef" :model="loginForm" :rules="loginRules" label-position="top" @keyup.enter="validateAndSubmit">
      <el-form-item label="账号" prop="username">
        <el-input :model-value="loginForm.username" placeholder="请输入10位账号" maxlength="10" clearable @update:model-value="loginForm.username = String($event)" />
      </el-form-item>
      <el-form-item label="密码" prop="password">
        <el-input
          :model-value="loginForm.password"
          type="password"
          placeholder="请输入8-20位密码"
          maxlength="20"
          show-password
          clearable
          @update:model-value="loginForm.password = String($event)"
        />
      </el-form-item>
      <el-button class="full-button auth-submit-btn" type="primary" :loading="loading" @click="validateAndSubmit">
        {{ authMode === 'login' ? '登录' : '注册并登录' }}
      </el-button>
    </el-form>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import type { FormInstance, FormRules } from 'element-plus';

const props = defineProps<{
  modelValue: boolean;
  authMode: 'login' | 'register';
  loginForm: {
    username: string;
    password: string;
  };
  loginRules: FormRules;
  loading: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'update:authMode', value: 'login' | 'register'): void;
  (e: 'submit'): void;
}>();

const loginFormRef = ref<FormInstance>();

const validateAndSubmit = async () => {
  if (!loginFormRef.value) return;
  try {
    await loginFormRef.value.validate();
    emit('submit');
  } catch (error) {
    // Element Plus keeps validation messages visible in the dialog.
  }
};
</script>

<style scoped lang="scss">
.login-head {
  position: relative;
  padding: 8px 0 4px;
}

.login-head::after {
  content: "";
  display: block;
  width: 100%;
  height: 1px;
  margin-top: 18px;
  background: linear-gradient(90deg, rgba(0, 240, 216, 0.55), rgba(157, 92, 255, 0.1), transparent);
}

.login-head strong {
  display: block;
  margin-top: 8px;
  color: var(--text);
  font-size: 30px;
  line-height: 1;
}

.login-head p {
  max-width: 320px;
  margin-top: 12px;
  color: var(--muted);
  line-height: 1.65;
}

.auth-switch {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 18px;
  padding: 4px;
  border: 1px solid rgba(0, 240, 216, 0.18);
  background: rgba(4, 9, 20, 0.62);
  border-radius: 8px;
}

.auth-switch button {
  border: 1px solid var(--line);
  background: rgba(8, 13, 26, 0.72);
  color: var(--muted);
  padding: 10px 14px;
  cursor: pointer;
  transition: border-color 0.3s ease, color 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease;
  border-radius: 8px;
}

.auth-switch button:hover,
.auth-switch button.active {
  color: var(--accent);
  background: rgba(0, 240, 216, 0.1);
  border-color: rgba(0, 240, 216, 0.55);
  box-shadow: 0 0 20px rgba(0, 240, 216, 0.12);
}

.auth-tab {
  min-height: 42px;
  border-color: rgba(0, 240, 216, 0.28) !important;
  font-weight: 800;
  color: #a9e4ff !important;
}

.auth-tab.active {
  color: #06101d !important;
  border-color: rgba(0, 240, 216, 0.88) !important;
  background: linear-gradient(135deg, #00f0d8, #22b8ff) !important;
  box-shadow: 0 0 26px rgba(0, 240, 216, 0.3) !important;
}

.full-button {
  width: 100%;
}

:global(.login-overlay) {
  background: rgba(2, 5, 12, 0.84) !important;
  backdrop-filter: blur(8px) saturate(120%);
}

:global(.login-dialog.el-dialog),
:global(.login-dialog .el-dialog) {
  width: min(92vw, 480px);
  background:
    linear-gradient(145deg, rgba(0, 240, 216, 0.08), rgba(157, 92, 255, 0.045) 46%, rgba(5, 10, 22, 0.18)),
    linear-gradient(180deg, rgba(6, 12, 26, 0.98), rgba(8, 13, 26, 0.98)) !important;
  border: 1px solid rgba(0, 240, 216, 0.34) !important;
  border-radius: 8px;
  box-shadow: 0 30px 120px rgba(0, 0, 0, 0.62), 0 0 44px rgba(0, 240, 216, 0.12) !important;
  backdrop-filter: blur(26px) saturate(125%);
}

:global(.login-dialog .el-dialog__header),
:global(.login-dialog.el-dialog .el-dialog__header),
:global(.login-dialog .el-dialog__body),
:global(.login-dialog.el-dialog .el-dialog__body) {
  background: transparent !important;
}

:global(.login-dialog .el-dialog__body),
:global(.login-dialog.el-dialog .el-dialog__body) {
  padding: 18px 28px 28px !important;
}

:global(.login-dialog .el-dialog__header),
:global(.login-dialog.el-dialog .el-dialog__header) {
  padding: 28px 28px 0 !important;
  margin-right: 0 !important;
}

:global(.login-dialog .el-dialog__headerbtn),
:global(.login-dialog.el-dialog .el-dialog__headerbtn) {
  top: 18px !important;
  right: 18px !important;
  width: 42px !important;
  height: 42px !important;
  border: 1px solid rgba(255, 107, 122, 0.58);
  background: rgba(255, 107, 122, 0.12);
  border-radius: 8px;
  box-shadow: 0 0 24px rgba(255, 107, 122, 0.14);
  transition: border-color 0.25s ease, background-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease;
}

:global(.login-dialog .el-dialog__headerbtn:hover),
:global(.login-dialog.el-dialog .el-dialog__headerbtn:hover) {
  border-color: rgba(255, 107, 122, 0.9);
  background: rgba(255, 107, 122, 0.2);
  box-shadow: 0 0 32px rgba(255, 107, 122, 0.26);
  transform: translateY(-1px);
}

:global(.login-dialog .el-dialog__close),
:global(.login-dialog.el-dialog .el-dialog__close) {
  color: #ff8b97 !important;
  font-size: 22px !important;
  font-weight: 900 !important;
}

:global(.login-dialog .el-form-item__label) {
  color: var(--muted);
}

:global(.login-dialog .el-input__wrapper) {
  border-radius: 8px;
  background: transparent !important;
  box-shadow: 0 0 0 1px rgba(0, 240, 216, 0.32) inset !important;
}

:global(.login-dialog .el-input__inner) {
  background: transparent !important;
  color: #fff !important;
  caret-color: var(--accent);
  -webkit-text-fill-color: #fff !important;
  transition: background-color 9999s ease-out 0s;
}

:global(.login-dialog .el-input__inner:-webkit-autofill),
:global(.login-dialog .el-input__inner:-webkit-autofill:hover),
:global(.login-dialog .el-input__inner:-webkit-autofill:focus) {
  -webkit-text-fill-color: #fff !important;
  box-shadow: 0 0 0 1000px rgba(5, 10, 22, 0.92) inset !important;
  caret-color: var(--accent);
}

:global(.login-dialog .el-form-item) {
  margin-bottom: 20px;
}

:global(.login-dialog .full-button) {
  min-height: 50px;
  margin-top: 6px;
  font-weight: 900;
}

:global(.login-dialog .auth-submit-btn) {
  --el-button-bg-color: #00f0d8;
  --el-button-border-color: rgba(0, 240, 216, 0.9);
  --el-button-text-color: #06101d;
  --el-button-hover-bg-color: #64fff1;
  --el-button-hover-border-color: #22b8ff;
  --el-button-hover-text-color: #06101d;
  box-shadow: 0 0 28px rgba(0, 240, 216, 0.26), 0 12px 28px rgba(0, 0, 0, 0.28);
}

:global(.login-dialog .el-input__wrapper:hover),
:global(.login-dialog .el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1px rgba(0, 240, 216, 0.56) inset, 0 0 22px rgba(0, 240, 216, 0.12) !important;
}
</style>
